// ============================================
// COMPANIES PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Search, Building2, Star, Briefcase, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCompanies, useDebounce } from '@/hooks';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/companies/')({
  component: CompaniesPage,
});

function CompaniesPage() {
  const { t } = useTranslation(['panel', 'common']);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 12;

  const debouncedSearch = useDebounce(search, 300);

  const { data: companiesResponse, isLoading } = useCompanies({
    page, limit,
    search: debouncedSearch || undefined,
  });

  const companiesData: any = (companiesResponse as any)?.data || companiesResponse || {};
  const companies: any[] = companiesData?.data || companiesData || [];
  const total = companiesData?.meta?.total || companiesData?.total || 0;
  const totalPages = companiesData?.meta?.totalPages || Math.ceil(total / limit);

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-maritime-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('panel:companies.title')}</h1>
        <p className="text-gray-500 mt-1">{t('panel:companies.activeCount', { count: total })}</p>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('panel:companies.searchPlaceholder')}
            className="input pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Companies Grid */}
      {companies.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('panel:companies.notFound')}</h3>
          <p className="text-gray-500">{t('panel:companies.tryDifferentSearch')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((company: any) => (
            <Link
              key={company.id}
              to="/companies/$slug"
              params={{ slug: company.slug }}
              className="card p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
                  {company.logoUrl ? (
                    <img src={company.logoUrl} alt={company.name} className="w-full h-full object-cover rounded-maritime" />
                  ) : (
                    <Building2 className="w-7 h-7 text-maritime-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{company.name}</h3>
                  <p className="text-sm text-gray-500">{company.country}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    {company.averageRating > 0 && (
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        {company.averageRating.toFixed(1)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {t('panel:companies.activeJobs', { count: company.activeJobsCount || 0 })}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary btn-sm">
            <ChevronLeft className="w-4 h-4" />{t('common:previous')}
          </button>
          <span className="text-sm text-gray-600">{t('common:pagination', { page, totalPages })}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-secondary btn-sm">
            {t('common:next')}<ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
